'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { formValidation } from '@/lib/formValidation'
import { sanitizeHTML } from '@/lib/utils'
import Breadcrumbs from '@/components/Secondary/Breadcrumbs/Breadcrumbs'
import { SimpleReCaptcha } from '@/lib/recaptcha/components'

interface ContactFormPageProps {
	data?: {
		heading?: {
			title?: string
		}
		text?: {
			text?: string
		}
	}
}

type FormData = {
	firstName: string
	lastName: string
	email: string
	phone: string
	message: string
	marketing: boolean
	recaptchaToken: string
}

export const fields = [
	// headings
	'heading.title',
	'text.text',
] as const

export default function ContactFormPage({ data }: ContactFormPageProps) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [formError, setFormError] = useState<string>('')
	const [formSuccess, setFormSuccess] = useState<string>('')
	const [recaptchaToken, setRecaptchaToken] = useState<string>('')
	const router = useRouter()

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<FormData>({
		mode: 'onBlur',
	})

	const handleRecaptchaVerify = (token: string) => {
		setRecaptchaToken(token)
		setValue('recaptchaToken', token)
		setFormError('')
	}

	const handleRecaptchaError = (error: string) => {
		// Не сбрасываем токен при ошибке загрузки, только при ошибке верификации
		if (error.includes('expired') || error.includes('Please complete')) {
			setRecaptchaToken('')
			setValue('recaptchaToken', '')
		}
		setFormError(error)
	}

	const onSubmit = async (data: FormData) => {
		// Check if reCAPTCHA is completed
		if (!recaptchaToken && !data.recaptchaToken) {
			setFormError('Please complete the reCAPTCHA verification')
			return
		}

		setIsSubmitting(true)
		setFormError('')
		setFormSuccess('')

		try {
			const response = await fetch('/api/contact-form-page/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ...data, recaptchaToken: recaptchaToken || data.recaptchaToken }),
			})

			if (!response.ok) throw new Error()
			// Redirect to thank you page immediately after successful submission
			router.push('/thank-you')
		} catch (error) {
			setFormError('Failed to submit. Please try again.')
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<>
			<Breadcrumbs data={data} />
			<section className='ContactFormPage w-full py-8 md:py-32 bg-neutral-50'>
				<div className='container'>
					<div className='p-6 md:p-16 bg-white flex flex-col gap-12'>
						{/* Form Header */}
						<div className='flex flex-col gap-3'>
							<h2>{data?.heading?.title || ''}</h2>
							{data?.text?.text && (
								<div
									className='text-base font-brandon'
									dangerouslySetInnerHTML={sanitizeHTML(data.text.text)}
								/>
							)}
						</div>

						{/* Form */}
						<form
							onSubmit={handleSubmit(onSubmit)}
							className='flex flex-col gap-6'
						>
							<input type='hidden' {...register('recaptchaToken')} />

							{/* First row of inputs */}
							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
								<div className='flex flex-col gap-3'>
									<label className='flex'>
										First Name<span>*</span>
									</label>
									<input
										{...register('firstName', { required: true })}
										type='text'
										placeholder='Enter your first name.'
										className='h-12 p-3 bg-white outline outline-1 outline-[#242424] placeholder-[#878787]'
										aria-required='true'
									/>
								</div>

								<div className='flex flex-col gap-3'>
									<label className='flex'>
										Last Name<span>*</span>
									</label>
									<input
										{...register('lastName', { required: true })}
										type='text'
										placeholder='Enter your last name.'
										className='h-12 p-3 bg-white outline outline-1 outline-[#242424] placeholder-[#878787]'
										aria-required='true'
									/>
								</div>

								<div className='flex flex-col gap-3'>
									<label className='flex'>
										Phone Number<span>*</span>
									</label>
									<div className='relative'>
										<div className='absolute left-3 top-1/2 -translate-y-1/2'>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='19'
												height='20'
												viewBox='0 0 19 20'
												fill='none'
											>
												<path
													d='M14.7122 10.7901C14.6194 10.6971 14.5092 10.6234 14.3879 10.5731C14.2666 10.5228 14.1365 10.4969 14.0052 10.4969C13.8739 10.4969 13.7438 10.5228 13.6225 10.5731C13.5012 10.6234 13.391 10.6971 13.2982 10.7901L11.7042 12.3841C10.9652 12.1641 9.58622 11.6641 8.71222 10.7901C7.83822 9.91609 7.33822 8.53709 7.11822 7.79809L8.71222 6.20409C8.80516 6.1113 8.8789 6.0011 8.92921 5.87978C8.97952 5.75847 9.00542 5.62842 9.00542 5.49709C9.00542 5.36576 8.97952 5.23571 8.92921 5.1144C8.8789 4.99308 8.80516 4.88288 8.71222 4.79009L4.71222 0.790088C4.61943 0.697143 4.50922 0.623405 4.38791 0.573094C4.26659 0.522783 4.13655 0.496887 4.00522 0.496887C3.87388 0.496887 3.74384 0.522783 3.62252 0.573094C3.50121 0.623405 3.391 0.697143 3.29822 0.790088L0.586217 3.50209C0.206217 3.88209 -0.00778346 4.40409 0.000216545 4.93709C0.0232165 6.36109 0.400217 11.3071 4.29822 15.2051C8.19622 19.1031 13.1422 19.4791 14.5672 19.5031H14.5952C15.1232 19.5031 15.6222 19.2951 16.0002 18.9171L18.7122 16.2051C18.8052 16.1123 18.8789 16.0021 18.9292 15.8808C18.9795 15.7595 19.0054 15.6294 19.0054 15.4981C19.0054 15.3668 18.9795 15.2367 18.9292 15.1154C18.8789 14.9941 18.8052 14.8839 18.7122 14.7911L14.7122 10.7901ZM14.5852 17.5021C13.3372 17.4811 9.06722 17.1461 5.71222 13.7901C2.34622 10.4241 2.02022 6.13909 2.00022 4.91609L4.00522 2.91109L6.59122 5.49709L5.29822 6.79009C5.18068 6.90754 5.09426 7.05243 5.04677 7.21166C4.99928 7.37089 4.99222 7.53944 5.02622 7.70209C5.05022 7.81709 5.63722 10.5441 7.29722 12.2041C8.95722 13.8641 11.6842 14.4511 11.7992 14.4751C11.9618 14.51 12.1305 14.5035 12.2899 14.4562C12.4492 14.4088 12.5941 14.3221 12.7112 14.2041L14.0052 12.9111L16.5912 15.4971L14.5852 17.5021Z'
													fill='#242424'
												/>
											</svg>
										</div>
										<input
											{...register('phone', {
												required: true,
												pattern:
													/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
											})}
											type='tel'
											placeholder='Enter your phone number.'
											className='h-12 p-3 pl-10 w-full bg-white outline outline-1 outline-[#242424] placeholder-[#a6a6a6]'
											aria-required='true'
										/>
									</div>
								</div>

								<div className='flex flex-col gap-3'>
									<label className='flex'>
										Email Address<span>*</span>
									</label>
									<div className='relative'>
										<div className='absolute left-3 top-1/2 -translate-y-1/2'>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='24'
												height='24'
												viewBox='0 0 24 24'
												fill='none'
											>
												<path
													d='M20 4H4C2.897 4 2 4.897 2 6V18C2 19.103 2.897 20 4 20H20C21.103 20 22 19.103 22 18V6C22 4.897 21.103 4 20 4ZM20 6V6.511L12 12.734L4 6.512V6H20ZM4 18V9.044L11.386 14.789C11.5611 14.9265 11.7773 15.0013 12 15.0013C12.2227 15.0013 12.4389 14.9265 12.614 14.789L20 9.044L20.002 18H4Z'
													fill='#242424'
												/>
											</svg>
										</div>
										<input
											{...register('email', {
												required: true,
												pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
											})}
											type='email'
											placeholder='Enter your email address.'
											className='h-12 p-3 pl-10 w-full bg-white outline outline-1 outline-[#242424] placeholder-[#a6a6a6]'
											aria-required='true'
										/>
									</div>
								</div>
							</div>

							{/* Message input */}
							<div className='flex flex-col gap-3'>
								<label className='flex'>
									Message<span>*</span>
								</label>
								<textarea
									{...register('message', { required: true })}
									placeholder='Enter your message.'
									rows={4}
									className='p-3 bg-white outline outline-1 outline-[#242424] placeholder-[#878787] resize-none'
									aria-required='true'
								/>
							</div>

							{/* Marketing Consent */}
							<div className='flex items-start gap-3'>
								<input
									type='checkbox'
									{...register('marketing', { required: true })}
									className='mt-1 h-4 w-4 accent-black'
									aria-required='true'
									aria-invalid={errors.marketing ? 'true' : 'false'}
								/>
								<span className='text-sm font-brandon text-black leading-normal'>
									I consent to receive phone calls, text messages, and emails
									from Harmon Facial Plastic Surgery.
								</span>
							</div>
							{errors.marketing && (
								<span className='text-red-500 text-sm font-brandon'>
									Please check the consent box to submit the form.
								</span>
							)}

							{/* Form error and success messages */}
							{formError && (
								<div className='text-red-500 text-sm'>{formError}</div>
							)}
							{formSuccess && (
								<div className='text-green-500 text-sm'>{formSuccess}</div>
							)}
						</form>

						{/* reCAPTCHA and Submit button */}
						<div className='md:flex md:justify-between md:items-center'>
							{/* reCAPTCHA */}
							<SimpleReCaptcha
								onVerify={handleRecaptchaVerify}
								onError={handleRecaptchaError}
								className=''
							/>
							
							{/* Submit button */}
							<button
								onClick={handleSubmit(onSubmit)}
								disabled={isSubmitting}
								className='px-12 py-4 bg-brown-light rounded-lg text-neutral-50 text-2xl font-brandon hover:bg-brown transition-colors disabled:opacity-50 w-full md:w-fit max-md:mt-4'
							>
								Submit
							</button>
						</div>
					</div>
				</div>
			</section>
		</>
	)
}
